import { useI18n } from '@change-corgi/core-react/i18n';

import { useLoginState } from '#src/app/shared/hooks/session';
import type { PaymentType } from '#src/app/shared/utils/payments';
import { validateEmail, validateFirstName, validateLastName, validateName } from '#src/app/shared/utils/payments';

import { usePromoteFormData } from '../../../formContext/formData';
import { usePromoteFormState } from '../../../formContext/formState';
import { useCanUserUpdateFields } from '../useCanUserUpdateFields';

import { useValidateAmount } from './hooks/useValidateAmount';

type ValidationResult = {
	valid: boolean;
	errors: Record<string, string>;
};

export function useValidationResult(): (paymentType: PaymentType) => ValidationResult {
	const { typedTranslate } = useI18n();
	const [{ firstName, lastName, name }] = usePromoteFormState();
	// Note: email comes from formData.user.email since formState doesn't have email
	// Note: usingSavedPaymentMethod is not in formState, defaulting to false for name validation
	const { user } = usePromoteFormData();
	const loginState = useLoginState();
	const canUserUpdateFields = useCanUserUpdateFields();
	const validateAmount = useValidateAmount();

	return function validationResult(paymentType): ValidationResult {
		const amountValidation = validateAmount();
		let emailValidationI18nKey;
		let firstNameValidationI18nKey;
		let lastNameValidationI18nKey;
		let nameValidationI18nKey;
		if (canUserUpdateFields(paymentType)) {
			emailValidationI18nKey = validateEmail({ email: user.email, loginState });
			firstNameValidationI18nKey = validateFirstName({ firstName, loginState });
			lastNameValidationI18nKey = validateLastName({ lastName, loginState });
			// usingSavedPaymentMethod is not in formState, defaulting to false
			nameValidationI18nKey = validateName({ name, selectedPaymentType: paymentType, usingSavedPaymentMethod: false });
		}

		const errors = {
			...amountValidation,
			...(emailValidationI18nKey && { email: typedTranslate(emailValidationI18nKey) }),
			...(firstNameValidationI18nKey && { firstName: typedTranslate(firstNameValidationI18nKey) }),
			...(lastNameValidationI18nKey && { lastName: typedTranslate(lastNameValidationI18nKey) }),
			...(nameValidationI18nKey && { name: typedTranslate(nameValidationI18nKey) }),
		};

		return {
			valid: !Object.values(errors).length,
			errors,
		};
	};
}
